
interface ProductFiltersProps {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  selectedSubcategory: string;
  setSelectedSubcategory: (subcategory: string) => void;
  priceRange: number[];
  setPriceRange: (range: number[]) => void;
  selectedColors: string[];
  setSelectedColors: (colors: string[]) => void;
  selectedSizes: string[];
  setSelectedSizes: (sizes: string[]) => void;
  sortBy: string;
  setSortBy: (sort: string) => void;
}

export default function ProductFilters({
  selectedCategory,
  setSelectedCategory,
  selectedSubcategory,
  setSelectedSubcategory,
  priceRange,
  setPriceRange,
  selectedColors,
  setSelectedColors,
  selectedSizes,
  setSelectedSizes,
  sortBy,
  setSortBy
}: ProductFiltersProps) {
  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'apparel', label: 'Apparel' },
    { value: 'accessories', label: 'Accessories' },
    { value: 'drinkware', label: 'Drinkware' },
    { value: 'gear', label: 'Gear' }
  ];

  const subcategories = {
    apparel: [
      { value: 'all', label: 'All Apparel' },
      { value: 'long-sleeves', label: 'Long Sleeves' },
      { value: 'shorts', label: 'Shorts' },
      { value: 'performance-shirts', label: 'Performance Shirts' },
      { value: 'jackets', label: 'Jackets' }
    ],
    accessories: [
      { value: 'all', label: 'All Accessories' },
      { value: 'caps', label: 'Caps & Hats' },
      { value: 'watch-bands', label: 'Watch Bands' },
      { value: 'decals', label: 'Decals' },
      { value: 'patches', label: 'Patches' }
    ],
    drinkware: [
      { value: 'all', label: 'All Drinkware' },
      { value: 'thermos', label: 'Thermos' },
      { value: 'bottles', label: 'Water Bottles' },
      { value: 'coolers', label: 'Coolers' },
      { value: 'tumblers', label: 'Tumblers' }
    ],
    gear: [
      { value: 'all', label: 'All Gear' },
      { value: 'bags', label: 'Tackle Bags' },
      { value: 'tools', label: 'Tool Kits' },
      { value: 'cases', label: 'Rod Cases' },
      { value: 'storage', label: 'Storage' }
    ]
  };

  const colors = [
    'Turquoise', 'Black', 'White', 'Navy', 'Orange', 'Khaki', 
    'Gray', 'Red', 'Blue', 'Green', 'Purple', 'Pink', 'Yellow', 'Brown', 'Olive'
  ];

  const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'One Size', '16oz', '24oz', '32oz', 'Small', 'Medium', 'Large'];

  const sortOptions = [
    { value: 'featured', label: 'Featured' },
    { value: 'newest', label: 'Newest' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'name', label: 'Name A-Z' },
    { value: 'rating', label: 'Highest Rated' }
  ];

  const handleColorToggle = (color: string) => {
    setSelectedColors(prev => 
      prev.includes(color) 
        ? prev.filter(c => c !== color)
        : [...prev, color]
    );
  };

  const handleSizeToggle = (size: string) => {
    setSelectedSizes(prev => 
      prev.includes(size) 
        ? prev.filter(s => s !== size)
        : [...prev, size]
    );
  };

  const getColorClass = (color: string) => {
    const colorMap: { [key: string]: string } = {
      'Turquoise': 'bg-teal-500',
      'Black': 'bg-black',
      'White': 'bg-white border-gray-300',
      'Navy': 'bg-blue-900',
      'Orange': 'bg-orange-500',
      'Khaki': 'bg-yellow-600',
      'Gray': 'bg-gray-500',
      'Red': 'bg-red-500',
      'Blue': 'bg-blue-500',
      'Green': 'bg-green-500',
      'Purple': 'bg-purple-500',
      'Pink': 'bg-pink-500',
      'Yellow': 'bg-yellow-500',
      'Brown': 'bg-yellow-800',
      'Olive': 'bg-green-700'
    };
    return colorMap[color] || 'bg-gray-400';
  };

  const clearAllFilters = () => {
    setSelectedCategory('all');
    setSelectedSubcategory('all');
    setPriceRange([0, 200]);
    setSelectedColors([]);
    setSelectedSizes([]);
    setSortBy('featured');
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-gray-900">Filters</h3>
        <button 
          onClick={clearAllFilters}
          className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer"
        >
          Clear All
        </button>
      </div>

      {/* Sort By */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">Sort By</label>
        <select 
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm pr-8 cursor-pointer"
        >
          {sortOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {/* Category */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">Category</label>
        <div className="space-y-2">
          {categories.map(category => (
            <label key={category.value} className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="category"
                value={category.value}
                checked={selectedCategory === category.value}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedSubcategory('all');
                }}
                className="mr-3 text-teal-600 cursor-pointer"
              />
              <span className="text-sm text-gray-700">{category.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Subcategory */}
      {selectedCategory !== 'all' && subcategories[selectedCategory as keyof typeof subcategories] && (
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Subcategory</label>
          <div className="space-y-2">
            {subcategories[selectedCategory as keyof typeof subcategories].map(subcategory => (
              <label key={subcategory.value} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="subcategory"
                  value={subcategory.value}
                  checked={selectedSubcategory === subcategory.value}
                  onChange={(e) => setSelectedSubcategory(e.target.value)}
                  className="mr-3 text-teal-600 cursor-pointer"
                />
                <span className="text-sm text-gray-700">{subcategory.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Price Range */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Price Range: ${priceRange[0]} - ${priceRange[1]}
        </label>
        <div className="space-y-3">
          <input
            type="range"
            min="0"
            max="200"
            value={priceRange[0]}
            onChange={(e) => setPriceRange([parseInt(e.target.value), priceRange[1]])}
            className="w-full cursor-pointer"
          />
          <input
            type="range"
            min="0"
            max="200"
            value={priceRange[1]}
            onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
            className="w-full cursor-pointer"
          />
        </div>
      </div>

      {/* Colors */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">Colors</label>
        <div className="grid grid-cols-5 gap-2">
          {colors.map(color => (
            <button
              key={color}
              onClick={() => handleColorToggle(color)}
              className={`w-8 h-8 rounded-full border-2 cursor-pointer ${
                selectedColors.includes(color) ? 'border-teal-600' : 'border-gray-300'
              } ${getColorClass(color)}`}
              title={color}
            ></button>
          ))}
        </div>
        {selectedColors.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {selectedColors.map(color => (
              <span key={color} className="text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded">
                {color}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Sizes */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">Sizes</label>
        <div className="grid grid-cols-3 gap-2">
          {sizes.map(size => (
            <button
              key={size}
              onClick={() => handleSizeToggle(size)}
              className={`px-3 py-2 border rounded text-sm cursor-pointer transition-colors ${
                selectedSizes.includes(size) 
                  ? 'border-teal-600 text-teal-600 bg-teal-50' 
                  : 'border-gray-300 hover:border-teal-600 hover:text-teal-600'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
